import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, ImageIcon, Save, RefreshCcw, Plus, Eye, EyeOff, GripVertical, Trash2 } from "lucide-react";
import { listSiteContent, updateSiteContent, uploadImage, uploadMedia, absoluteUrl } from "@/lib/api";
import { SOCIAL_PLATFORMS, getPlatform, SocialIcon } from "@/lib/socials";
import { Switch } from "@/components/ui/switch";

// Sections grouped by public page. Each page becomes a top-level tab; a second
// pill row lets the admin jump between sections within that page.
const PAGES = [
  {
    key: "home",
    label: "Home",
    tid: "content-page-home",
    sections: [
      { key: "home-hero", label: "Hero" },
      { key: "home-story", label: "01 · Our Story" },
      { key: "home-chef", label: "02 · The Kitchen (Chef)" },
      { key: "home-menu-teaser", label: "03 · On The Menu" },
      { key: "home-reserve", label: "04 · Reserve" },
    ],
  },
  {
    key: "location",
    label: "Location",
    tid: "content-page-location",
    sections: [
      { key: "location-hero", label: "Hero" },
      { key: "location-info", label: "Info · Hours & tips" },
    ],
  },
  {
    key: "menus",
    label: "Menus",
    tid: "content-page-menus",
    sections: [
      { key: "menus-hero", label: "Hero" },
    ],
  },
  {
    key: "reservation",
    label: "Reservation",
    tid: "content-page-reservation",
    sections: [
      { key: "reservation-hero", label: "Hero" },
    ],
  },
  {
    key: "contact",
    label: "Contact",
    tid: "content-page-contact",
    sections: [
      { key: "contact-hero", label: "Hero" },
      { key: "contact-info", label: "Info · phone, email, socials" },
    ],
  },
  {
    key: "footer",
    label: "Footer",
    tid: "content-page-footer",
    sections: [
      { key: "footer", label: "Site footer" },
    ],
  },
];

// Flat list still used by SectionEditor for lookups.
const SECTIONS = PAGES.flatMap((p) =>
  p.sections.map((s) => ({ ...s, page: p.key, overline: `${p.label} · ${s.label}` }))
);

const inputCls =
  "w-full bg-transparent border-b border-border rounded-none px-0 py-3 text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary transition-colors font-sans text-sm";

// Field that accepts either an image OR a video. Used for the Story quote
// card background — the caller passes both the image slot and the video slot,
// and this component ensures only one is set at a time.
function MediaField({ label, imageValue, videoValue, onImageChange, onVideoChange, testid, hint }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const currentUrl = videoValue || imageValue;
  const isVideo = !!videoValue;

  async function pickFile(file) {
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadMedia(file);
      if (res.media_type === "video") {
        onVideoChange(res.url || res.image_url);
        onImageChange(null);
      } else {
        onImageChange(res.url || res.image_url);
        onVideoChange(null);
      }
      toast.success("Uploaded — click Save to apply.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  function clearAll() {
    onImageChange(null);
    onVideoChange(null);
  }

  return (
    <div>
      <label className="overline block mb-3 flex items-center justify-between gap-3">
        <span>{label}</span>
        {hint && (
          <span className="normal-case tracking-normal text-[0.65rem] italic text-muted-foreground/80">
            {hint}
          </span>
        )}
      </label>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*"
        className="hidden"
        data-testid={`${testid}-input`}
        onChange={(e) => pickFile(e.target.files?.[0])}
      />
      {currentUrl ? (
        <div className="flex items-start gap-4">
          <div className="w-40 h-28 border border-border overflow-hidden bg-black">
            {isVideo ? (
              <video
                src={absoluteUrl(currentUrl)}
                className="w-full h-full object-cover"
                muted
                playsInline
                autoPlay
                loop
                data-testid={`${testid}-preview-video`}
              />
            ) : (
              <img
                src={absoluteUrl(currentUrl)}
                alt=""
                className="w-full h-full object-cover"
                data-testid={`${testid}-preview-image`}
              />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              data-testid={`${testid}-replace`}
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 px-3 py-2 border border-border text-[0.68rem] uppercase tracking-[0.2em] text-foreground/85 hover:border-primary hover:text-primary transition-colors disabled:opacity-50 w-fit"
            >
              <Upload size={11} strokeWidth={1.5} />
              {uploading ? "Uploading…" : "Replace"}
            </button>
            <button
              type="button"
              data-testid={`${testid}-remove`}
              onClick={clearAll}
              disabled={uploading}
              className="inline-flex items-center gap-2 px-3 py-2 text-[0.68rem] uppercase tracking-[0.2em] text-muted-foreground hover:text-destructive transition-colors w-fit"
            >
              <X size={11} strokeWidth={1.5} />
              Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          data-testid={`${testid}-upload`}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="w-full flex items-center justify-center gap-3 border border-dashed border-border py-6 text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
        >
          {uploading ? (
            <><Upload size={14} strokeWidth={1.5} className="animate-pulse" /> Uploading…</>
          ) : (
            <><ImageIcon size={14} strokeWidth={1.5} /> Upload image or video</>
          )}
        </button>
      )}
    </div>
  );
}

function ImageField({ label, value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  async function pickFile(file) {
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadImage(file);
      onChange(res.image_url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div>
      {label && <label className="overline block mb-3">{label}</label>}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        data-testid={`${testid}-input`}
        onChange={(e) => pickFile(e.target.files?.[0])}
      />
      {value ? (
        <div className="flex items-start gap-4">
          <div className="w-28 h-28 border border-border overflow-hidden bg-black">
            <img
              src={absoluteUrl(value)}
              alt=""
              className="w-full h-full object-cover"
              data-testid={`${testid}-preview`}
            />
          </div>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              data-testid={`${testid}-replace`}
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 px-3 py-2 border border-border text-[0.68rem] uppercase tracking-[0.2em] text-foreground/85 hover:border-primary hover:text-primary transition-colors disabled:opacity-50 w-fit"
            >
              <Upload size={11} strokeWidth={1.5} />
              {uploading ? "Uploading…" : "Replace"}
            </button>
            <button
              type="button"
              data-testid={`${testid}-remove`}
              onClick={() => onChange(null)}
              disabled={uploading}
              className="inline-flex items-center gap-2 px-3 py-2 text-[0.68rem] uppercase tracking-[0.2em] text-muted-foreground hover:text-destructive transition-colors w-fit"
            >
              <X size={11} strokeWidth={1.5} />
              Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          data-testid={`${testid}-upload`}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="w-full flex items-center justify-center gap-3 border border-dashed border-border py-6 text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
        >
          {uploading ? (
            <><Upload size={14} strokeWidth={1.5} className="animate-pulse" /> Uploading…</>
          ) : (
            <><ImageIcon size={14} strokeWidth={1.5} /> Upload image</>
          )}
        </button>
      )}
    </div>
  );
}

function SectionEditor({ section, data, onSaved }) {
  const [f, setF] = useState(data);
  const [saving, setSaving] = useState(false);

  useEffect(() => setF(data), [data]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const setImg = (k) => (val) => setF((s) => ({ ...s, [k]: val }));

  async function save() {
    setSaving(true);
    try {
      const payload = {
        overline: f.overline || "",
        title_top: f.title_top || "",
        title_italic: f.title_italic || "",
        title_bottom: f.title_bottom || "",
        body: f.body || "",
        body_secondary: f.body_secondary || "",
        cta_label: f.cta_label || "",
        cta_url: f.cta_url || "",
        quote_text: f.quote_text || "",
        quote_label: f.quote_label || "",
        title_visible: f.title_visible !== false,
        body_visible: f.body_visible !== false,
        image_primary: f.image_primary,
        image_secondary: f.image_secondary,
        image_tertiary: f.image_tertiary,
        video_primary: f.video_primary,
        video_secondary: f.video_secondary,
        video_tertiary: f.video_tertiary,
      };
      if (section.key === "home-chef") {
        payload.chef_name = f.chef_name || "";
        payload.chef_title = f.chef_title || "";
      }
      if (section.key === "reservation-hero") {
        payload.reservation_notes_label = f.reservation_notes_label || "";
        payload.reservation_notes_body = f.reservation_notes_body || "";
        payload.reservation_dress_label = f.reservation_dress_label || "";
        payload.reservation_dress_body = f.reservation_dress_body || "";
        payload.reservation_enquiries_label = f.reservation_enquiries_label || "";
        payload.reservation_phone = f.reservation_phone || "";
      }
      const updated = await updateSiteContent(section.key, payload);
      onSaved(updated);
      toast.success(`${section.label} updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  const isChef = section.key === "home-chef";
  const isHomeHero = section.key === "home-hero";
  const heroSections = [
    "home-hero", "menus-hero", "reservation-hero", "contact-hero", "location-hero",
  ];
  const isHero = heroSections.includes(section.key);
  const showQuote = section.key === "home-story" || isChef;
  const showSecondaryBody = section.key === "home-story" || section.key === "home-menu-teaser";
  const showSecondaryImage = section.key === "home-story";
  const showBottomLine = section.key === "home-story" || isHomeHero;
  const noCtaSections = ["contact-hero", "location-hero", "reservation-hero"];
  const noImageSections = ["contact-hero", "location-hero", "reservation-hero", "home-hero"];
  const showCta = !noCtaSections.includes(section.key) && !isChef;
  const showImages = !noImageSections.includes(section.key);

  return (
    <div className="space-y-8">
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <label className="overline block mb-3">Overline</label>
          <input
            data-testid={`content-${section.key}-overline`}
            className={inputCls}
            value={f.overline || ""}
            onChange={set("overline")}
            placeholder="e.g. 01 — Our Story"
          />
        </div>
        {showCta && (
          <div>
            <label className="overline block mb-3">CTA URL</label>
            <input
              data-testid={`content-${section.key}-cta-url`}
              className={inputCls}
              value={f.cta_url || ""}
              onChange={set("cta_url")}
              placeholder="/menus"
            />
          </div>
        )}
      </div>

      {isHero && (
        <div className="flex items-center justify-between gap-3 -mt-2 pb-1">
          <div className="overline text-primary/80">Hero title</div>
          <label className="flex items-center gap-3 text-[0.68rem] uppercase tracking-[0.2em] text-muted-foreground cursor-pointer">
            <span>{f.title_visible !== false ? "Visible" : "Hidden"}</span>
            <Switch
              data-testid={`content-${section.key}-title-visible`}
              checked={f.title_visible !== false}
              onCheckedChange={(v) => setF((s) => ({ ...s, title_visible: v }))}
            />
          </label>
        </div>
      )}

      <div className={`grid md:grid-cols-3 gap-8 ${isHero && f.title_visible === false ? "opacity-50" : ""}`}>
        <div>
          <label className="overline block mb-3">Title (top)</label>
          <input
            data-testid={`content-${section.key}-title-top`}
            className={inputCls}
            value={f.title_top || ""}
            onChange={set("title_top")}
          />
        </div>
        <div>
          <label className="overline block mb-3">Title (italic)</label>
          <input
            data-testid={`content-${section.key}-title-italic`}
            className={inputCls}
            value={f.title_italic || ""}
            onChange={set("title_italic")}
          />
        </div>
        {showBottomLine && (
          <div>
            <label className="overline block mb-3">Title (bottom)</label>
            <input
              data-testid={`content-${section.key}-title-bottom`}
              className={inputCls}
              value={f.title_bottom || ""}
              onChange={set("title_bottom")}
            />
          </div>
        )}
      </div>

      {isHero && (
        <div className="flex items-center justify-between gap-3 pt-2 pb-1">
          <div className="overline text-primary/80">Hero subtitle</div>
          <label className="flex items-center gap-3 text-[0.68rem] uppercase tracking-[0.2em] text-muted-foreground cursor-pointer">
            <span>{f.body_visible !== false ? "Visible" : "Hidden"}</span>
            <Switch
              data-testid={`content-${section.key}-body-visible`}
              checked={f.body_visible !== false}
              onCheckedChange={(v) => setF((s) => ({ ...s, body_visible: v }))}
            />
          </label>
        </div>
      )}

      <div className={isHero && f.body_visible === false ? "opacity-50" : ""}>
        <label className="overline block mb-3 flex items-center justify-between gap-3">
          <span>Body</span>
          <span className="normal-case tracking-normal text-[0.65rem] italic text-muted-foreground/80">
            Press Enter for a line break · leave a blank line for a new paragraph
          </span>
        </label>
        <textarea
          data-testid={`content-${section.key}-body`}
          rows={7}
          className={inputCls + " resize-y min-h-[9rem] leading-relaxed"}
          value={f.body || ""}
          onChange={set("body")}
        />
      </div>

      {showSecondaryBody && (
        <div>
          <label className="overline block mb-3 flex items-center justify-between gap-3">
            <span>Body (secondary paragraph)</span>
            <span className="normal-case tracking-normal text-[0.65rem] italic text-muted-foreground/80">
              Optional — shown below the main body in a softer tone
            </span>
          </label>
          <textarea
            data-testid={`content-${section.key}-body-secondary`}
            rows={5}
            className={inputCls + " resize-y min-h-[7rem] leading-relaxed"}
            value={f.body_secondary || ""}
            onChange={set("body_secondary")}
          />
        </div>
      )}

      {showCta && (
        <div>
          <label className="overline block mb-3">CTA Label</label>
          <input
            data-testid={`content-${section.key}-cta-label`}
            className={inputCls}
            value={f.cta_label || ""}
            onChange={set("cta_label")}
            placeholder="Read the Menus"
          />
        </div>
      )}

      {showQuote && (
        <div className="pt-6 border-t border-border">
          <div className="overline mb-4">Quote card</div>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <label className="overline block mb-3">Quote label (small)</label>
              <input
                data-testid={`content-${section.key}-quote-label`}
                className={inputCls}
                value={f.quote_label || ""}
                onChange={set("quote_label")}
                placeholder="Ceylon"
              />
            </div>
            <div>
              <label className="overline block mb-3">Quote text (italic)</label>
              <input
                data-testid={`content-${section.key}-quote-text`}
                className={inputCls}
                value={f.quote_text || ""}
                onChange={set("quote_text")}
                placeholder="A story in every bite."
              />
            </div>
          </div>

          {section.key === "home-story" && (
            <div className="mt-8">
              <MediaField
                label="Quote card background"
                imageValue={f.image_tertiary}
                videoValue={f.video_tertiary}
                onImageChange={setImg("image_tertiary")}
                onVideoChange={setImg("video_tertiary")}
                testid={`content-${section.key}-quote-media`}
                hint="Optional — image or video sits behind the label & italic text"
              />
            </div>
          )}
        </div>
      )}

      {isChef && (
        <div className="pt-6 border-t border-border grid md:grid-cols-2 gap-8">
          <div>
            <label className="overline block mb-3">Chef name</label>
            <input
              data-testid="content-home-chef-name"
              className={inputCls}
              value={f.chef_name || ""}
              onChange={set("chef_name")}
              placeholder="Manjula Kuruwagama"
            />
          </div>
          <div>
            <label className="overline block mb-3">Chef title</label>
            <input
              data-testid="content-home-chef-title"
              className={inputCls}
              value={f.chef_title || ""}
              onChange={set("chef_title")}
              placeholder="Head Chef"
            />
          </div>
        </div>
      )}

      {section.key === "reservation-hero" && (
        <div className="pt-6 border-t border-border space-y-8">
          <div>
            <div className="overline text-primary/80 mb-6">Reservation content sections</div>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <label className="overline block mb-3">Notes label</label>
                <input
                  data-testid="content-reservation-notes-label"
                  className={inputCls}
                  value={f.reservation_notes_label || ""}
                  onChange={set("reservation_notes_label")}
                  placeholder="01 — Notes"
                />
              </div>
              <div>
                <label className="overline block mb-3">Notes text</label>
                <textarea
                  data-testid="content-reservation-notes-body"
                  rows={3}
                  className={inputCls + " resize-y min-h-[5rem] leading-relaxed"}
                  value={f.reservation_notes_body || ""}
                  onChange={set("reservation_notes_body")}
                />
              </div>
              <div>
                <label className="overline block mb-3">Dress label</label>
                <input
                  data-testid="content-reservation-dress-label"
                  className={inputCls}
                  value={f.reservation_dress_label || ""}
                  onChange={set("reservation_dress_label")}
                  placeholder="02 — Dress"
                />
              </div>
              <div>
                <label className="overline block mb-3">Dress text</label>
                <textarea
                  data-testid="content-reservation-dress-body"
                  rows={3}
                  className={inputCls + " resize-y min-h-[5rem] leading-relaxed"}
                  value={f.reservation_dress_body || ""}
                  onChange={set("reservation_dress_body")}
                />
              </div>
              <div>
                <label className="overline block mb-3">Enquiries label</label>
                <input
                  data-testid="content-reservation-enquiries-label"
                  className={inputCls}
                  value={f.reservation_enquiries_label || ""}
                  onChange={set("reservation_enquiries_label")}
                  placeholder="03 — Enquiries"
                />
              </div>
              <div>
                <label className="overline block mb-3">Enquiries phone</label>
                <input
                  data-testid="content-reservation-phone"
                  className={inputCls}
                  value={f.reservation_phone || ""}
                  onChange={set("reservation_phone")}
                  placeholder="+62 21 1234 5678"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {showImages && (
        <div className="pt-6 border-t border-border grid md:grid-cols-2 gap-10">
          {section.key === "home-story" ? (
            <MediaField
              label="Primary · image or video"
              imageValue={f.image_primary}
              videoValue={f.video_primary}
              onImageChange={setImg("image_primary")}
              onVideoChange={setImg("video_primary")}
              testid={`content-${section.key}-media-primary`}
            />
          ) : (
            <ImageField
              label="Image · primary"
              value={f.image_primary}
              onChange={setImg("image_primary")}
              testid={`content-${section.key}-image-primary`}
            />
          )}
          {showSecondaryImage && (
            section.key === "home-story" ? (
              <MediaField
                label="Secondary · image or video"
                imageValue={f.image_secondary}
                videoValue={f.video_secondary}
                onImageChange={setImg("image_secondary")}
                onVideoChange={setImg("video_secondary")}
                testid={`content-${section.key}-media-secondary`}
              />
            ) : (
              <ImageField
                label="Image · secondary"
                value={f.image_secondary}
                onChange={setImg("image_secondary")}
                testid={`content-${section.key}-image-secondary`}
              />
            )
          )}
        </div>
      )}

      <div className="pt-4 flex justify-end">
        <button
          data-testid={`content-${section.key}-save`}
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground text-[0.72rem] uppercase tracking-[0.22em] font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          <Save size={12} strokeWidth={1.5} />
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

function ContactInfoEditor({ section, data, onSaved }) {
  const [f, setF] = useState(data);
  const [saving, setSaving] = useState(false);

  useEffect(() => setF(data), [data]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  // -------- Socials helpers --------
  const socials = Array.isArray(f.socials) ? f.socials : [];

  const updateSocials = (updater) =>
    setF((s) => ({ ...s, socials: updater(Array.isArray(s.socials) ? s.socials : []) }));

  const addSocial = () => {
    const first = SOCIAL_PLATFORMS[0];
    updateSocials((list) => [
      ...list,
      {
        id: (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID()
          : `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        platform: first.value,
        url: "",
        handle: "",
        is_active: true,
        order: list.length,
      },
    ]);
  };

  const patchSocial = (id, patch) =>
    updateSocials((list) => list.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const removeSocial = (id) =>
    updateSocials((list) =>
      list.filter((s) => s.id !== id).map((s, i) => ({ ...s, order: i })),
    );

  const moveSocial = (id, dir) =>
    updateSocials((list) => {
      const sorted = [...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const idx = sorted.findIndex((s) => s.id === id);
      const target = idx + dir;
      if (idx < 0 || target < 0 || target >= sorted.length) return list;
      [sorted[idx], sorted[target]] = [sorted[target], sorted[idx]];
      return sorted.map((s, i) => ({ ...s, order: i }));
    });

  async function save() {
    setSaving(true);
    try {
      const payload = {
        phone: f.phone || "",
        hours_label: f.hours_label || "",
        email_general: f.email_general || "",
        email_events: f.email_events || "",
        email_press: f.email_press || "",
        address_line1: f.address_line1 || "",
        address_line2: f.address_line2 || "",
        socials: socials.map((s, i) => ({
          id: s.id,
          platform: s.platform || "other",
          url: s.url || "",
          handle: s.handle || "",
          is_active: s.is_active !== false,
          order: i,
        })),
      };
      const updated = await updateSiteContent(section.key, payload);
      onSaved(updated);
      toast.success(`${section.label} updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-10">
      {/* Reservations */}
      <div className="border border-border p-6 space-y-6">
        <div className="overline">Reservations</div>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <label className="overline block mb-3">Phone number</label>
            <input
              data-testid="content-contact-info-phone"
              className={inputCls}
              value={f.phone || ""}
              onChange={set("phone")}
              placeholder="+62 21 1234 5678"
            />
          </div>
          <div>
            <label className="overline block mb-3">Hours label</label>
            <input
              data-testid="content-contact-info-hours-label"
              className={inputCls}
              value={f.hours_label || ""}
              onChange={set("hours_label")}
              placeholder="Daily, 12:00 – 22:00"
            />
          </div>
        </div>
      </div>

      {/* Emails */}
      <div className="border border-border p-6 space-y-6">
        <div className="overline">Email addresses</div>
        <div className="grid md:grid-cols-3 gap-8">
          <div>
            <label className="overline block mb-3">General</label>
            <input
              data-testid="content-contact-info-email-general"
              className={inputCls}
              value={f.email_general || ""}
              onChange={set("email_general")}
              placeholder="hello@nelumdining.com"
            />
          </div>
          <div>
            <label className="overline block mb-3">Private events</label>
            <input
              data-testid="content-contact-info-email-events"
              className={inputCls}
              value={f.email_events || ""}
              onChange={set("email_events")}
              placeholder="events@nelumdining.com"
            />
          </div>
          <div>
            <label className="overline block mb-3">Press</label>
            <input
              data-testid="content-contact-info-email-press"
              className={inputCls}
              value={f.email_press || ""}
              onChange={set("email_press")}
              placeholder="press@nelumdining.com"
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground italic">
          Leave a field blank to hide that email row on the public Contact page.
        </p>
      </div>

      {/* Address */}
      <div className="border border-border p-6 space-y-6">
        <div className="overline">Visit</div>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <label className="overline block mb-3">Address line 1</label>
            <input
              data-testid="content-contact-info-address-line1"
              className={inputCls}
              value={f.address_line1 || ""}
              onChange={set("address_line1")}
              placeholder="Jl. Suryo No. 24"
            />
          </div>
          <div>
            <label className="overline block mb-3">Address line 2</label>
            <input
              data-testid="content-contact-info-address-line2"
              className={inputCls}
              value={f.address_line2 || ""}
              onChange={set("address_line2")}
              placeholder="Senopati, South Jakarta"
            />
          </div>
        </div>
      </div>

      {/* Socials */}
      <div className="border border-border p-6 space-y-5" data-testid="content-contact-info-socials">
        <div className="flex items-center justify-between">
          <div>
            <div className="overline">Social links</div>
            <p className="text-xs text-muted-foreground italic mt-1">
              Toggle inactive to hide a link on Contact + Footer without deleting it.
            </p>
          </div>
          <button
            type="button"
            data-testid="content-contact-info-add-social"
            onClick={addSocial}
            className="inline-flex items-center gap-2 px-3 py-2 border border-border text-[0.68rem] uppercase tracking-[0.2em] text-foreground/85 hover:border-primary hover:text-primary transition-colors"
          >
            <Plus size={12} strokeWidth={1.5} />
            Add social
          </button>
        </div>

        {socials.length === 0 ? (
          <div className="text-sm text-muted-foreground italic py-6 text-center border border-dashed border-border">
            No social links yet — click <em>Add social</em> to create one.
          </div>
        ) : (
          <div className="space-y-3">
            {[...socials]
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
              .map((s, i, arr) => {
                const p = getPlatform(s.platform);
                const inactive = s.is_active === false;
                return (
                  <div
                    key={s.id}
                    data-testid={`content-social-row-${s.platform}-${s.id}`}
                    className={`grid grid-cols-[auto_1fr_1fr_auto] items-center gap-3 border p-3 transition-colors ${
                      inactive
                        ? "border-border/60 bg-card/40 opacity-70"
                        : "border-border bg-card/70"
                    }`}
                  >
                    {/* Reorder handle */}
                    <div className="flex flex-col items-center text-muted-foreground">
                      <button
                        type="button"
                        aria-label="Move up"
                        onClick={() => moveSocial(s.id, -1)}
                        disabled={i === 0}
                        className="disabled:opacity-30 hover:text-foreground p-0.5"
                      >
                        <GripVertical size={12} strokeWidth={1.5} className="rotate-90" />
                      </button>
                    </div>

                    {/* Platform + URL column */}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-foreground/70"><SocialIcon platform={s.platform} size={14} /></span>
                        <select
                          data-testid={`content-social-platform-${s.id}`}
                          value={s.platform}
                          onChange={(e) => patchSocial(s.id, { platform: e.target.value })}
                          className="bg-transparent border-b border-border text-xs text-foreground focus:outline-none focus:border-primary py-0.5"
                        >
                          {SOCIAL_PLATFORMS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <input
                        data-testid={`content-social-url-${s.id}`}
                        className={inputCls + " text-xs"}
                        value={s.url || ""}
                        onChange={(e) => patchSocial(s.id, { url: e.target.value })}
                        placeholder={p.placeholder_url}
                      />
                    </div>

                    {/* Handle column */}
                    <div className="min-w-0">
                      <label className="text-[0.6rem] uppercase tracking-[0.18em] text-muted-foreground block mb-1.5">
                        Display handle
                      </label>
                      <input
                        data-testid={`content-social-handle-${s.id}`}
                        className={inputCls + " text-xs"}
                        value={s.handle || ""}
                        onChange={(e) => patchSocial(s.id, { handle: e.target.value })}
                        placeholder={p.placeholder_handle}
                      />
                    </div>

                    {/* Actions column */}
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        data-testid={`content-social-toggle-${s.id}`}
                        onClick={() => patchSocial(s.id, { is_active: inactive })}
                        aria-pressed={!inactive}
                        title={inactive ? "Activate" : "Deactivate"}
                        className={`inline-flex items-center justify-center w-9 h-9 border transition-colors ${
                          inactive
                            ? "border-border text-muted-foreground hover:border-primary hover:text-primary"
                            : "border-primary/70 text-primary bg-primary/10"
                        }`}
                      >
                        {inactive ? <EyeOff size={13} strokeWidth={1.5} /> : <Eye size={13} strokeWidth={1.5} />}
                      </button>
                      <button
                        type="button"
                        data-testid={`content-social-remove-${s.id}`}
                        onClick={() => removeSocial(s.id)}
                        aria-label="Remove"
                        className="inline-flex items-center justify-center w-9 h-9 border border-border text-muted-foreground hover:border-destructive hover:text-destructive transition-colors"
                      >
                        <Trash2 size={13} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      <div className="pt-4 flex justify-end">
        <button
          data-testid="content-contact-info-save"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground text-[0.72rem] uppercase tracking-[0.22em] font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          <Save size={12} strokeWidth={1.5} />
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

function LocationInfoEditor({ section, data, onSaved }) {
  const [f, setF] = useState(data);
  const [saving, setSaving] = useState(false);

  useEffect(() => setF(data), [data]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  // -------- Hours helpers --------
  const hours = Array.isArray(f.hours) ? f.hours : [];

  const updateHours = (updater) =>
    setF((s) => ({ ...s, hours: updater(Array.isArray(s.hours) ? s.hours : []) }));

  const addHour = () =>
    updateHours((list) => [
      ...list,
      {
        _rid: (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID()
          : `h-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        day: "",
        time: "",
      },
    ]);

  const patchHour = (idx, patch) =>
    updateHours((list) => list.map((h, i) => (i === idx ? { ...h, ...patch } : h)));

  const removeHour = (idx) =>
    updateHours((list) => list.filter((_, i) => i !== idx));

  const moveHour = (idx, dir) =>
    updateHours((list) => {
      const target = idx + dir;
      if (target < 0 || target >= list.length) return list;
      const next = [...list];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });

  async function save() {
    setSaving(true);
    try {
      const payload = {
        hours: hours.map((h) => ({ day: h.day || "", time: h.time || "" })),
        by_car: f.by_car || "",
        by_mrt: f.by_mrt || "",
        closing_quote: f.closing_quote || "",
        map_query: f.map_query || "",
        image_primary: f.image_primary,
        video_primary: f.video_primary,
      };
      const updated = await updateSiteContent(section.key, payload);
      onSaved(updated);
      toast.success(`${section.label} updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-10">
      {/* Opening hours */}
      <div className="border border-border p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="overline">Opening hours</div>
            <p className="text-xs text-muted-foreground italic mt-1">
              Use &ldquo;Closed&rdquo; as the time to render the row in italic on the public page.
            </p>
          </div>
          <button
            type="button"
            data-testid="content-location-add-hour"
            onClick={addHour}
            className="inline-flex items-center gap-2 px-3 py-2 border border-border text-[0.68rem] uppercase tracking-[0.2em] text-foreground/85 hover:border-primary hover:text-primary transition-colors"
          >
            <Plus size={12} strokeWidth={1.5} />
            Add row
          </button>
        </div>
        {hours.length === 0 ? (
          <div className="text-sm text-muted-foreground italic py-6 text-center border border-dashed border-border">
            No hours set — click <em>Add row</em> to add opening days.
          </div>
        ) : (
          <div className="space-y-3">
            {hours.map((h, i) => (
              <div
                key={h._rid || `${h.day}-${h.time}-${i}`}
                data-testid={`content-location-hour-row-${i}`}
                className="grid grid-cols-[auto_1fr_1fr_auto] items-center gap-3 border border-border p-3 bg-card/70"
              >
                <div className="flex flex-col items-center text-muted-foreground">
                  <button
                    type="button"
                    aria-label="Move up"
                    onClick={() => moveHour(i, -1)}
                    disabled={i === 0}
                    className="disabled:opacity-30 hover:text-foreground p-0.5"
                  >
                    <GripVertical size={12} strokeWidth={1.5} className="rotate-90" />
                  </button>
                </div>
                <div>
                  <label className="text-[0.6rem] uppercase tracking-[0.18em] text-muted-foreground block mb-1.5">
                    Day / range
                  </label>
                  <input
                    data-testid={`content-location-hour-day-${i}`}
                    className={inputCls + " text-xs"}
                    value={h.day || ""}
                    onChange={(e) => patchHour(i, { day: e.target.value })}
                    placeholder="Tuesday – Thursday"
                  />
                </div>
                <div>
                  <label className="text-[0.6rem] uppercase tracking-[0.18em] text-muted-foreground block mb-1.5">
                    Time
                  </label>
                  <input
                    data-testid={`content-location-hour-time-${i}`}
                    className={inputCls + " text-xs"}
                    value={h.time || ""}
                    onChange={(e) => patchHour(i, { time: e.target.value })}
                    placeholder="18:00 – 23:00 · or Closed"
                  />
                </div>
                <button
                  type="button"
                  data-testid={`content-location-hour-remove-${i}`}
                  onClick={() => removeHour(i)}
                  aria-label="Remove row"
                  className="inline-flex items-center justify-center w-9 h-9 border border-border text-muted-foreground hover:border-destructive hover:text-destructive transition-colors"
                >
                  <Trash2 size={13} strokeWidth={1.5} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Arrival */}
      <div className="border border-border p-6 space-y-6">
        <div className="overline">Arrival tips</div>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <label className="overline block mb-3">By car</label>
            <textarea
              data-testid="content-location-by-car"
              rows={3}
              className={inputCls + " resize-none"}
              value={f.by_car || ""}
              onChange={set("by_car")}
              placeholder="Parking & valet notes"
            />
          </div>
          <div>
            <label className="overline block mb-3">By MRT / public transport</label>
            <textarea
              data-testid="content-location-by-mrt"
              rows={3}
              className={inputCls + " resize-none"}
              value={f.by_mrt || ""}
              onChange={set("by_mrt")}
              placeholder="Nearest station & walking directions"
            />
          </div>
        </div>
      </div>

      {/* Map + closing */}
      <div className="border border-border p-6 space-y-6">
        <div className="overline">Map & closing</div>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <label className="overline block mb-3">Google Maps query</label>
            <input
              data-testid="content-location-map-query"
              className={inputCls}
              value={f.map_query || ""}
              onChange={set("map_query")}
              placeholder="Jalan Suryo, Senopati, Kebayoran Baru, South Jakarta"
            />
            <p className="text-xs text-muted-foreground italic mt-2">
              The address the embedded map centres on. Uses Google Maps directly.
            </p>
          </div>
          <div>
            <label className="overline block mb-3">Closing quote</label>
            <input
              data-testid="content-location-closing-quote"
              className={inputCls}
              value={f.closing_quote || ""}
              onChange={set("closing_quote")}
              placeholder="See you at the table"
            />
            <p className="text-xs text-muted-foreground italic mt-2">
              The italic line over the bottom image. Leave empty to hide the block.
            </p>
          </div>
        </div>
        <div className="pt-4 border-t border-border/60">
          <MediaField
            label="Closing background · image or video"
            imageValue={f.image_primary}
            videoValue={f.video_primary}
            onImageChange={(val) => setF((s) => ({ ...s, image_primary: val }))}
            onVideoChange={(val) => setF((s) => ({ ...s, video_primary: val }))}
            testid="content-location-closing-media"
          />
          <p className="text-xs text-muted-foreground italic mt-2">
            Displayed under the map with the closing quote overlaid. Landscape works best · 1600×900 or wider.
            Uploading either replaces the other.
          </p>
        </div>
      </div>

      <div className="pt-4 flex justify-end">
        <button
          data-testid="content-location-info-save"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground text-[0.72rem] uppercase tracking-[0.22em] font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          <Save size={12} strokeWidth={1.5} />
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

function FooterEditor({ section, data, onSaved }) {
  const [f, setF] = useState(data);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => setF(data), [data]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const partnerLogos = Array.isArray(f.partner_logos) ? f.partner_logos : [];

  async function addLogo(file) {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const res = await uploadImage(file);
      setF((s) => ({
        ...s,
        partner_logos: [...(Array.isArray(s.partner_logos) ? s.partner_logos : []), res.image_url],
      }));
      toast.success("Logo added — click Save to publish.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingLogo(false);
    }
  }

  function removeLogo(idx) {
    setF((s) => ({
      ...s,
      partner_logos: (s.partner_logos || []).filter((_, i) => i !== idx),
    }));
  }

  function moveLogo(idx, dir) {
    setF((s) => {
      const arr = [...(s.partner_logos || [])];
      const to = idx + dir;
      if (to < 0 || to >= arr.length) return s;
      [arr[idx], arr[to]] = [arr[to], arr[idx]];
      return { ...s, partner_logos: arr };
    });
  }

  async function save() {
    setSaving(true);
    try {
      const payload = {
        body: f.body || "",
        closing_quote: f.closing_quote || "",
        copyright_text: f.copyright_text || "",
        partner_logos: partnerLogos,
        group_text: f.group_text || "",
        group_logo: f.group_logo || null,
        group_url: f.group_url || "",
      };
      const updated = await updateSiteContent(section.key, payload);
      onSaved(updated);
      toast.success(`${section.label} updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-10">
      <div className="border border-border p-6 space-y-6">
        <div className="overline">Under the logo</div>
        <div>
          <label className="overline block mb-3 flex items-center justify-between gap-3">
            <span>Description</span>
            <span className="normal-case tracking-normal text-[0.65rem] italic text-muted-foreground/80">
              Press Enter for a line break · leave a blank line for a new paragraph
            </span>
          </label>
          <textarea
            data-testid="content-footer-body"
            rows={5}
            className={inputCls + " resize-y min-h-[7rem] leading-relaxed"}
            value={f.body || ""}
            onChange={set("body")}
            placeholder="Modern Sri Lankan cuisine in the heart of Senopati…"
          />
        </div>
      </div>

      <div className="border border-border p-6 space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="overline">Partner / featured logos</div>
            <p className="text-xs text-muted-foreground italic mt-2 leading-relaxed">
              Small row of images shown centered in the footer (media features, awards,
              partners). PNGs with transparent backgrounds look best.
            </p>
          </div>
          <input
            ref={logoInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="content-footer-partner-input"
            onChange={(e) => addLogo(e.target.files?.[0])}
          />
          <button
            type="button"
            data-testid="content-footer-partner-upload"
            onClick={() => logoInputRef.current?.click()}
            disabled={uploadingLogo}
            className="inline-flex items-center gap-2 px-4 py-2 border border-border text-[0.68rem] uppercase tracking-[0.2em] text-foreground/85 hover:border-primary hover:text-primary transition-colors disabled:opacity-50 shrink-0"
          >
            <Upload size={11} strokeWidth={1.5} />
            {uploadingLogo ? "Uploading…" : "Add logo"}
          </button>
        </div>

        {partnerLogos.length === 0 ? (
          <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            No partner logos yet — click <span className="text-foreground/85">Add logo</span> to upload one.
          </div>
        ) : (
          <div
            data-testid="content-footer-partner-list"
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3"
          >
            {partnerLogos.map((url, i) => (
              <div
                key={`${url}-${i}`}
                data-testid={`content-footer-partner-item-${i}`}
                className="group relative border border-border bg-card/50 aspect-[3/2] flex items-center justify-center overflow-hidden"
              >
                <img
                  src={absoluteUrl(url)}
                  alt={`Partner ${i + 1}`}
                  className="max-w-[85%] max-h-[80%] object-contain"
                />
                <div className="absolute inset-0 bg-background/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                  <button
                    type="button"
                    data-testid={`content-footer-partner-up-${i}`}
                    onClick={() => moveLogo(i, -1)}
                    disabled={i === 0}
                    className="px-2 py-1 border border-border text-[0.6rem] uppercase tracking-[0.2em] text-foreground/80 hover:border-primary hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move left"
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    data-testid={`content-footer-partner-down-${i}`}
                    onClick={() => moveLogo(i, 1)}
                    disabled={i === partnerLogos.length - 1}
                    className="px-2 py-1 border border-border text-[0.6rem] uppercase tracking-[0.2em] text-foreground/80 hover:border-primary hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move right"
                  >
                    →
                  </button>
                  <button
                    type="button"
                    data-testid={`content-footer-partner-remove-${i}`}
                    onClick={() => removeLogo(i)}
                    className="px-2 py-1 border border-border text-[0.6rem] uppercase tracking-[0.2em] text-muted-foreground hover:border-destructive hover:text-destructive"
                    title="Remove"
                  >
                    <X size={11} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border border-border p-6 space-y-6">
        <div>
          <div className="overline">Part of a group</div>
          <p className="text-xs text-muted-foreground italic mt-2 leading-relaxed">
            Optional attribution shown centered inside the copyright bar. If a logo is uploaded,
            it&rsquo;s used and the wording becomes the alt text. If only wording is set, the
            wording is shown.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <label className="overline block mb-3">Wording</label>
            <input
              data-testid="content-footer-group-text"
              className={inputCls}
              value={f.group_text || ""}
              onChange={set("group_text")}
              placeholder="Part of LDS Group"
            />
          </div>
          <div>
            <label className="overline block mb-3 flex items-center justify-between gap-3">
              <span>Logo (optional)</span>
              <span className="normal-case tracking-normal text-[0.65rem] italic text-muted-foreground/80">
                PNG or SVG · shown small — transparent works best
              </span>
            </label>
            <ImageField
              label={null}
              value={f.group_logo || ""}
              onChange={(v) => setF((s) => ({ ...s, group_logo: v || null }))}
              testid="content-footer-group-logo"
            />
          </div>
        </div>
        <div>
          <label className="overline block mb-3">Link (optional)</label>
          <input
            data-testid="content-footer-group-url"
            className={inputCls}
            value={f.group_url || ""}
            onChange={set("group_url")}
            placeholder="https://ldsgroup.com"
          />
          <p className="text-xs text-muted-foreground italic mt-2">
            When set, the wording or logo becomes a clickable link (opens in a new tab).
          </p>
        </div>
      </div>

      <div className="border border-border p-6 space-y-6">
        <div className="overline">Bottom bar</div>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <label className="overline block mb-3">Copyright text</label>
            <input
              data-testid="content-footer-copyright"
              className={inputCls}
              value={f.copyright_text || ""}
              onChange={set("copyright_text")}
              placeholder="Nelum Dining · Jakarta"
            />
            <p className="text-xs text-muted-foreground italic mt-2">
              Shown after &ldquo;© {new Date().getFullYear()}&rdquo; on the left.
            </p>
          </div>
          <div>
            <label className="overline block mb-3">Tagline</label>
            <input
              data-testid="content-footer-tagline"
              className={inputCls}
              value={f.closing_quote || ""}
              onChange={set("closing_quote")}
              placeholder="Taste the Soul of Sri Lanka"
            />
            <p className="text-xs text-muted-foreground italic mt-2">
              Small primary-coloured line on the right of the bottom bar.
            </p>
          </div>
        </div>
      </div>

      <div className="border border-border/60 p-6 space-y-3 bg-card/40">
        <div className="overline">Sourced from other sections</div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          <strong className="text-foreground/85">Visit address</strong> uses{" "}
          <em>Contact → Info · phone, email, socials → Visit</em>.
          <br />
          <strong className="text-foreground/85">Hours</strong> uses{" "}
          <em>Location → Info · Hours &amp; tips → Opening hours</em>.
          <br />
          <strong className="text-foreground/85">Social links</strong> use{" "}
          <em>Contact → Info · phone, email, socials → Social links</em>.
        </p>
      </div>

      <div className="pt-4 flex justify-end">
        <button
          data-testid="content-footer-save"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground text-[0.72rem] uppercase tracking-[0.22em] font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          <Save size={12} strokeWidth={1.5} />
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

export default function AdminContent() {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(PAGES[0].key);
  const [activeSection, setActiveSection] = useState(PAGES[0].sections[0].key);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listSiteContent();
      const map = {};
      list.forEach((c) => (map[c.section] = c));
      setContent(map);
    } catch {
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // When switching pages, reset the active section to that page's first entry.
  const selectPage = (pageKey) => {
    setActivePage(pageKey);
    const page = PAGES.find((p) => p.key === pageKey);
    if (page?.sections?.[0]) setActiveSection(page.sections[0].key);
  };

  const currentPage = PAGES.find((p) => p.key === activePage) || PAGES[0];
  const current = SECTIONS.find((s) => s.key === activeSection);
  const data = content[activeSection];
  const isContactInfo = activeSection === "contact-info";
  const isLocationInfo = activeSection === "location-info";
  const isFooter = activeSection === "footer";

  return (
    <div data-testid="page-admin-content">
      <main className="mx-auto max-w-[1400px] px-6 lg:px-12 py-14">
        <div className="flex items-end justify-between gap-8 mb-10">
          <div>
            <div className="overline">Content</div>
            <h1 className="mt-4 serif text-4xl md:text-5xl font-light leading-tight text-foreground">
              Every word, editable.
            </h1>
            <p className="mt-3 text-sm text-muted-foreground max-w-xl">
              Pick a page, then a section. Every headline, paragraph, quote and image on the public site is editable here.
            </p>
          </div>
          <button
            data-testid="admin-content-refresh"
            onClick={load}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-border text-[0.72rem] uppercase tracking-[0.22em] text-foreground/85 hover:border-primary hover:text-primary transition-colors"
          >
            <RefreshCcw size={12} strokeWidth={1.5} />
            Refresh
          </button>
        </div>

        {/* Level 1 · Page tabs */}
        <div
          className="mb-4 border-b border-border flex items-stretch overflow-x-auto"
          data-testid="content-page-tabs"
        >
          {PAGES.map((p) => {
            const active = activePage === p.key;
            return (
              <button
                key={p.key}
                data-testid={p.tid}
                onClick={() => selectPage(p.key)}
                className={`relative px-5 py-3 text-[0.72rem] uppercase tracking-[0.22em] font-semibold transition-colors whitespace-nowrap ${
                  active
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {p.label}
                {active && (
                  <span className="absolute left-3 right-3 -bottom-px h-[2px] bg-primary" />
                )}
                <span className="ml-2 text-[0.6rem] text-muted-foreground/70">
                  {p.sections.length}
                </span>
              </button>
            );
          })}
        </div>

        {/* Level 2 · Section pills for the active page */}
        <div className="mb-10 flex flex-wrap items-center gap-2 py-3">
          <span className="text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground mr-2">Section</span>
          {currentPage.sections.map((s) => {
            const active = activeSection === s.key;
            return (
              <button
                key={s.key}
                data-testid={`content-tab-${s.key}`}
                onClick={() => setActiveSection(s.key)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-[0.68rem] uppercase tracking-[0.2em] border transition-colors ${
                  active
                    ? "border-primary text-primary bg-primary/10"
                    : "border-border text-foreground/70 hover:text-foreground hover:border-foreground/50"
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </div>

        {loading || !data ? (
          <div className="py-24 text-center text-muted-foreground italic serif text-xl">
            <span className="ornament">Loading</span>
          </div>
        ) : isContactInfo ? (
          <ContactInfoEditor
            key={activeSection}
            section={current}
            data={data}
            onSaved={(updated) => setContent((c) => ({ ...c, [activeSection]: updated }))}
          />
        ) : isLocationInfo ? (
          <LocationInfoEditor
            key={activeSection}
            section={current}
            data={data}
            onSaved={(updated) => setContent((c) => ({ ...c, [activeSection]: updated }))}
          />
        ) : isFooter ? (
          <FooterEditor
            key={activeSection}
            section={current}
            data={data}
            onSaved={(updated) => setContent((c) => ({ ...c, [activeSection]: updated }))}
          />
        ) : (
          <SectionEditor
            key={activeSection}
            section={current}
            data={data}
            onSaved={(updated) => setContent((c) => ({ ...c, [activeSection]: updated }))}
          />
        )}
      </main>
    </div>
  );
}
